/**
 * ルート木の根。認証状態でアプリ本体を出し分け、サイドバーと PJ モードの案内を描く殻。
 *
 * 機能ごとのルート定義（`registry/*.ts`）はすべてこの根を親に取る。根を `app.tsx` に置くと、
 * 機能のルート定義が `app.tsx` を読み、`app.tsx` が機能のルート定義を読む循環になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ルート木の根を定義し、認証状態に応じてローディング / ログイン誘導 / 認証エラー / アプリ本体を出し分ける。
 * @graph-connects useAuth [calls] ログイン中ユーザー取得
 */
import { AppShell } from '@cortex/web-design-system/components/layout/AppShell';
import type { SidebarRenderNavLinkArgs } from '@cortex/web-design-system/components/layout/Sidebar';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Link, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageMenu } from '../../components/layout/LanguageMenu.js';
import { NavLogo, buildNavGroups, navLinkTarget } from '../../components/layout/nav-groups.js';
import { ThemeToggle } from '../../components/layout/ThemeToggle.js';
import { TermLinkProvider } from '../../components/spec/TermText.js';
import { useApprovalAccess } from '../../hooks/queries/spec-pj-queries.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useCurrentPj, usePjModeExit } from '../../hooks/usePjMode.js';
import { isUnauthenticatedError, redirectToEdgeAuth } from '../../lib/auth-redirect.js';
import { formatPjLabel } from '../../lib/pj-label.js';
import { isPjMode } from '../../lib/pj-mode.js';
import { readPjNumberParam, readPjSearchNumber } from '../receivers/pj-number.js';
import type { PjExitTarget } from '../pj-exit.js';
import { pjLinkSearch } from '../pj-link.js';
import { usePjMasterPresence } from '../pj-master-presence.js';
import { FLOW_ROUTE, PJS_ROUTE, PJ_DIFF_ROUTE, readsPjSearch } from '../route-paths.js';

/** @graph-connects none */
export const rootRoute = createRootRoute({
  component: RootLayout,
});

/**
 * サイドバーのナビ項目を描画する。
 *
 * 共有デザインシステムはナビ項目の `path` を `string` で持つので、ルーターの型付き `to` へ
 * 渡す前に実在ルートへ解決する必要がある。解決そのものは `navLinkTarget` に閉じてある。
 * 型アサーションで黙らせると、ナビ定義の綴り誤りが利用者側の 404 として初めて表面化する。
 *
 * ナビが指すのは静的ルート 7 本（承認一覧は担当者にだけ出る）と、PJ モードのときだけ現れる
 * PJ の変更一覧である。パスパラメータを持つのは後者だけで、遷移しないフォルダ行は持たない。
 *
 * **PJ を読む行だけが PJ を引き継ぐ。** 引き継がないと、PJ を選んで詳細を開いた利用者が
 * サイドバーで一覧へ移った瞬間に PJ が外れ、マスターを読んでいることが画面に出ないまま
 * 「PJ を適用した後の姿」を読んだつもりになる。業務マップと一覧はどれも `?pj=` を読むので、
 * 渡しさえすれば PJ を適用した後の姿が出る — 足りていなかったのは導線だけである。
 *
 * **PJ 一覧（`/pjs`）には渡さない。** そこは PJ を選ぶ入口であって PJ を読まない。
 * 渡すと、PJ を適用していない画面に PJ が効いているかのような URL と案内が残る。
 * 渡してよい相手は `readsPjSearch` が 1 箇所で決める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business サイドバーのナビ項目を、PJ を読む行にだけ PJ を引き継ぐリンクとして描画する。
 * @graph-connects navLinkTarget [calls] ナビ項目のパスを実在ルートへ解決する
 * @graph-connects readsPjSearch [calls] PJ を渡してよい相手かの判定
 * @graph-connects pjLinkSearch [calls] PJ を引き継ぐ検索条件
 */
function renderNavLink(
  { item, className, children, onNavigate }: SidebarRenderNavLinkArgs,
  pj: string | undefined,
) {
  const target = navLinkTarget(item.path);

  /*
   * PJ の変更一覧だけがパスパラメータで PJ を持つ。検索条件では渡さない — 同じ PJ が
   * パスと検索条件の 2 箇所に出ると、どちらが正かを読む側が決めることになる。
   */
  if (target.kind === 'pj-diff') {
    return (
      <Link
        to={PJ_DIFF_ROUTE}
        params={{ pjNumber: target.pjNumber }}
        className={className}
        onClick={onNavigate}
      >
        {children}
      </Link>
    );
  }

  const search = readsPjSearch(target.to) ? pjLinkSearch(pj) : {};

  return (
    <Link to={target.to} {...search} className={className} onClick={onNavigate}>
      {children}
    </Link>
  );
}

/** `PjModeBadge` の props。 @graph-connects none */
interface PjModeBadgeProps {
  /** いま見ている PJ 番号。 */
  pj: string;
  /** PJ モードを終えたときの行き先。 */
  exit: PjExitTarget;
}

/**
 * PJ モードに居ることを、画面をまたいで一目で見えるようにする。
 *
 * 差分の注記は詳細画面が出すが、一覧へ移るとそれが消える。PJ が効いているかどうかを
 * 画面で確かめられないと、利用者は「いま見ているのがマスターなのか PJ を適用した後なのか」を
 * URL を読んで判断することになる。
 *
 * **色の付いた囲みにする。** 以前は本文と同じ細い文字で 1 行書いていたが、通常の見出しや注記と
 * 見分けが付かず「PJ モードになったことが分からない」状態のままだった。PJ を適用した姿と
 * マスターは中身の見た目が同じなので、状態そのものを地の色で示さないと気付けない。色は既存の
 * CSS 変数（`--color-warning-*`）だけで組み、新しい意匠は作らない。
 *
 * **PJ 番号を主役に置き、選択・コピーできる形（`font-mono`）で出す。** 番号は他の道具
 * （Backlog・PR・Slack）へ持ち出す値なので、読めるだけでなく取り出せる必要がある。
 * 綴りは `formatPjLabel` が決める（`AIRCLOSET-139162`）。素の数だけを出していたときは、持ち出した
 * 先の課題キーと目で突き合わせられなかった。
 *
 * 外す導線を番号の隣に置く。入る導線（PJ サマリー）だけがあって出る導線が無いと、
 * マスターを見たい利用者が URL を手で削ることになる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いま PJ を適用した姿を見ていることと PJ 番号を色付きの囲みで示し、PJ モードを終える導線を隣に置く。
 * @graph-connects PjExitLink [calls] PJ モードを終える導線
 * @graph-connects formatPjLabel [calls] 案件番号の画面表記
 */
function PjModeBadge({ pj, exit }: PjModeBadgeProps) {
  const { t } = useTranslation();
  const pjLabel = formatPjLabel(pj);

  return (
    <Badge
      variant="warning"
      solid
      data-pj-mode={pj}
      title={t('pjMode.viewing', { pj: pjLabel })}
      className="gap-2 rounded-md px-3 py-1"
    >
      <span className="tracking-wide uppercase">{t('pjMode.label')}</span>
      <span className="font-mono font-bold">{pjLabel}</span>
      <PjExitLink exit={exit} pj={pj} label={t('pjMode.exit')} />
    </Badge>
  );
}

/** `PjExitLink` の props。 @graph-connects none */
interface PjExitLinkProps {
  /** PJ モードを終えたときの行き先。 */
  exit: PjExitTarget;
  /**
   * いま見ている PJ の綴り（検索条件かパスの親に載っているもの）。
   *
   * 検索条件で受ける詳細画面から外すとき、見ている節点が案件の新設かを引くのに使う。
   */
  pj: string;
  /** リンクの文言。 */
  label: string;
}

/**
 * PJ モードを終える導線を、行き先の種類ごとのリンクとして描く。
 *
 * **リンクで作る（ボタンにして `navigate` を呼ばない）。** 押す前に行き先が分かり、新しいタブで
 * 開けて、戻る操作で PJ モードへ戻れる。どこへ行くかの判断そのものは `pjExitTarget` が
 * 1 箇所で持ち、ここは受け取った種類をルーターが受ける形へ置き換えるだけである。種類ごとに
 * 分かれているのは、相対の `.`・静的パス・パスパラメータ付きでリンクの受ける形が違うためで、
 * 1 つに畳むと型付きの `to` へ渡すときにアサーションが要る。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ モードを終えたときの行き先を、種類に応じたリンクとして描く。
 * @graph-connects PjNodeExitLink [calls] 見ている節点が案件の新設なら案件サマリーへ倒す導線
 * @graph-connects readPjNumberParam [calls] 綴りを 1 以上の整数へ読む
 * @graph-connects readPjSearchNumber [calls] 検索条件の案件の綴りを番号へ読む
 */
function PjExitLink({ exit, pj, label }: PjExitLinkProps) {
  const className = 'underline underline-offset-2';

  if (exit.kind === 'pjsList') {
    return (
      <Link to={PJS_ROUTE} className={className}>
        {label}
      </Link>
    );
  }

  /*
   * 残る 2 種は「同じ節点のマスターへ外す」である。その節点が案件の新設ならマスターに姿が
   * 無いので、案件の変更の行を引いて確かめる。引くので、案件番号も節点の番号も契約と同じ関門
   * （1 以上の整数）を通ったものだけを別部品へ渡す。通らない綴りでそのまま引くと、読めない
   * 番号の問い合わせが飛ぶ。通らないときは今までどおりマスターへ送る（そこが URL の誤りを
   * 案内する）。
   */
  if (exit.kind === 'flow') {
    const master = (
      <Link to={FLOW_ROUTE} params={{ flowId: exit.flowId }} className={className}>
        {label}
      </Link>
    );
    const pjValue = readPjNumberParam(exit.pjNumber);
    const story = readPjNumberParam(exit.flowId);
    if (pjValue === null || story === null) return master;
    return (
      <PjNodeExitLink
        pjValue={pjValue}
        nodeValue={story}
        label={label}
        className={className}
        master={master}
      />
    );
  }

  const master = (
    <Link to="." search={{}} className={className}>
      {label}
    </Link>
  );
  /* 検索条件の案件は 2 通りの綴り（数 / `AIRCLOSET-<数>`）で届くので、受け口と同じ読み方をする。 */
  const pjValue = readPjSearchNumber(pj);
  const node = exit.nodeId === null ? null : readPjNumberParam(exit.nodeId);
  if (pjValue === null || node === null) return master;
  return (
    <PjNodeExitLink
      pjValue={pjValue}
      nodeValue={node}
      label={label}
      className={className}
      master={master}
    />
  );
}

/** `PjNodeExitLink` の props。 @graph-connects none */
interface PjNodeExitLinkProps {
  /** 案件の変更の行を引くための案件番号。案件サマリーのパスにも使う。 */
  pjValue: number;
  /** 案件の変更の行と突き合わせる、いま見ている節点の番号。 */
  nodeValue: number;
  /** リンクの文言。 */
  label: string;
  /** リンクの見た目。 */
  className: string;
  /** 節点がマスターに居るときの導線（同じ節点のマスターの姿へのリンク）。 */
  master: ReactNode;
}

/**
 * 節点 1 件を見ている画面から案件モードを外す導線。
 *
 * **案件が新設した節点にはマスターの姿が無い。** そこへ送ると「見つかりません」に着き、外す
 * 操作そのものが行き止まりになる（実測でストーリー 1052・用語 1427〜1430・業務 1050 / 1051）。
 * 案件が作ったかどうかは案件の変更の行に書いてあるので、作ったものなら案件サマリーへ倒す
 * （`pjExitTarget` が「業務フローが読めないときは PJ 一覧へ倒す」と決めているのと同じ判断を、読めるが
 * **マスターに居ない**場合へ広げたもの）。業務フローの図も、検索条件で案件を受ける詳細
 * （用語・画面・業務・業務ルール・マスター）も同じ部品を通す — 片方だけ直すと、経路によって
 * 外した先が「見つかりません」になったりならなかったりする。
 *
 * **判定が返るまではマスターの姿へ向ける。** 待ちのあいだ行き先を空にすると外す口が一瞬消える。
 * 変更の行は案件ごとに 1 本のキャッシュに乗るので、業務マップ・案件サマリー・フローを経て
 * 来た利用者には問い合わせが増えない。
 *
 * @param props 案件と節点の番号・文言・見た目・マスターへの導線
 * @returns 案件モードを外す導線
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードを外す導線を、見ている節点が案件の新設なら案件サマリーへ、そうでなければ同じ節点のマスターへ向ける。
 * @graph-connects usePjMasterPresence [calls] 案件が新設した節点かの判定
 */
function PjNodeExitLink({ pjValue, nodeValue, label, className, master }: PjNodeExitLinkProps) {
  const presence = usePjMasterPresence(pjValue, nodeValue);

  if (presence === 'absent') {
    return (
      <Link to={PJ_DIFF_ROUTE} params={{ pjNumber: String(pjValue) }} className={className}>
        {label}
      </Link>
    );
  }

  return master;
}

/**
 * 認証状態でルートを出し分ける。
 *
 * 未認証（401）は Edge Auth へフルページ遷移してログインさせる（`frontend.md` の Critical 規約）。
 * それ以外の失敗は redirect せずエラー表示に残す。API 障害まで redirect に倒すと、ログイン
 * できているのにログイン画面との間で往復し続けるうえ、セッション切れと API 障害の区別が
 * 利用者に付かなくなるため。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 認証状態に応じてローディング / ログイン誘導 / 認証エラー / アプリ本体を出し分ける。
 * @graph-connects useAuth [calls] ログイン中ユーザー取得
 * @graph-connects redirectToEdgeAuth [calls] 未認証時のログイン誘導
 * @graph-connects AuthErrorState [calls] 認証以外の失敗時のフォールバック
 * @graph-connects usePjModeExit [calls] PJ を読む画面かの判定と外した後の行き先
 * @graph-connects PjModeBadge [calls] PJ モードであることの表示と離脱導線
 * @graph-connects useApprovalAccess [calls] 本人が承認を積める担当者かの照合（サイドバーの管理の群の出し分け）
 */
function RootLayout() {
  const { t } = useTranslation();
  const { error, isAuthenticated, isLoading, user } = useAuth();
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const pj = useCurrentPj();
  /*
   * 承認一覧の入口は担当者にだけ出す。照合の前や失敗時は出さない — 担当者でない人に出して
   * 押した先で拒否されるより、担当者に一瞬遅れて出るほうが害が小さい。
   */
  const approvalAccess = useApprovalAccess();
  const canApprove = approvalAccess.data?.canApprove === true;
  /*
   * 案内を出すのは、いまの画面が PJ を読むときだけである。PJ を読まない画面（PJ 一覧など）で
   * 出すと、PJ を適用していない画面が「PJ を適用した後の姿」だと読める。URL を手で打って
   * `?pj=` を付けた場合もここで止まる。読む画面なら、外した後の行き先も同時に決まる。
   */
  const pjExit = usePjModeExit();
  const needsLogin = !isLoading && (isUnauthenticatedError(error) || (!error && !isAuthenticated));

  useEffect(() => {
    if (needsLogin) redirectToEdgeAuth();
  }, [needsLogin]);

  if (isLoading) {
    return <FullScreenNotice message={t('common.loading')} />;
  }

  if (needsLogin) {
    return <FullScreenNotice message={t('auth.redirecting')} />;
  }

  if (error) {
    return <AuthErrorState error={error} />;
  }

  return (
    <AppShell
      appName={t('app.name')}
      logo={<NavLogo />}
      navGroups={buildNavGroups((key) => t(key), pj, canApprove)}
      renderNavLink={(args) => renderNavLink(args, pj)}
      currentPath={currentPath}
      headerActions={
        <>
          {isPjMode(pj) && pjExit !== undefined && <PjModeBadge pj={pj} exit={pjExit} />}
          <LanguageMenu />
          <ThemeToggle />
        </>
      }
      userMenu={
        <span className="font-mono text-xs text-[var(--color-text-muted)]">{user?.email}</span>
      }
    >
      {/*
       * 用語辞書はここで 1 回だけ取り、配下の全画面へ配る。画面ごとに取ると、一覧から詳細へ
       * 移るたびに同じ辞書を引き直す。PJ を選んでいれば、その PJ が定義した用語も辞書へ
       * 入る（選んでいる PJ はこの層が既に知っている）。
       */}
      <TermLinkProvider pjNumber={pj}>
        <Outlet />
      </TermLinkProvider>
    </AppShell>
  );
}

/** @graph-connects none */
interface FullScreenNoticeProps {
  message: string;
}

/**
 * 画面全体を占める 1 行の案内（読み込み中 / ログイン画面へ遷移中）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 読み込み中・ログイン誘導中の状態を画面全体の案内として表示する。
 * @graph-connects none
 */
function FullScreenNotice({ message }: FullScreenNoticeProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-base)]">
      <span className="text-sm text-[var(--color-text-muted)]">{message}</span>
    </div>
  );
}

/** @graph-connects none */
interface AuthErrorStateProps {
  error: unknown;
}

/**
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 認証確認が認証以外の理由で失敗した状態を明示する。セッション切れと API 障害を利用者が区別できるようにする。
 * @graph-connects none
 */
function AuthErrorState({ error }: AuthErrorStateProps) {
  const { t } = useTranslation();
  const message = error instanceof Error ? error.message : t('common.error');

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-base)] p-6 text-[var(--color-text-primary)]">
      <Card className="w-full max-w-lg" padding="lg">
        <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-error)] uppercase">
          {t('auth.errorLabel')}
        </div>
        <h1 className="mt-3 text-2xl font-bold">{t('auth.errorTitle')}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {t('auth.errorDescription')}
        </p>
        <div className="mt-4 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-4 py-3 font-mono text-sm text-[var(--color-text-secondary)]">
          {message}
        </div>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('common.retry')}
        </Button>
      </Card>
    </div>
  );
}
